import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Truck, Pill, RefreshCw, Download, ShieldAlert, Filter, Mail, Phone, MapPin, Calendar } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "new",       label: "Nouveau",      color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "contacted", label: "Contacté",     color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "qualified", label: "Qualifié",     color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  { value: "signed",    label: "Signé",        color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "rejected",  label: "Rejeté",       color: "bg-slate-100 text-slate-600 border-slate-200" },
];

export default function AdminLeads() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ transport: 0, pharmacy: 0 });
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== "all") params.partner_type = filterType;
      if (filterStatus !== "all") params.status = filterStatus;
      const { data } = await api.get("/admin/leads", { params });
      setItems(data.items || []);
      setCounts(data.counts || { transport: 0, pharmacy: 0 });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType, filterStatus]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => [it.company_name, it.contact_name, it.email, it.city].some((f) => (f || "").toLowerCase().includes(q)));
  }, [items, search]);

  const updateStatus = async (lead_id, status) => {
    try {
      await api.patch(`/admin/leads/${lead_id}`, { status });
      setItems((arr) => arr.map((l) => (l.lead_id === lead_id ? { ...l, status } : l)));
      toast.success("Statut mis à jour");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const exportCSV = () => {
    const headers = ["lead_id","partner_type","company_name","siret","contact_name","email","phone","city","postal_code","region","fleet_or_size","message","status","consent_communications","source","created_at"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(",")].concat(filtered.map((r) => headers.map((h) => escape(r[h])).join(","))).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `doctobook_leads_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${filtered.length} lead(s) exporté(s)`);
  };

  if (user?.role !== "admin" && !user?.is_admin) {
    return (
      <div className="p-10 max-w-2xl mx-auto text-center">
        <ShieldAlert className="h-12 w-12 mx-auto text-red-500 mb-3" />
        <h1 className="font-display text-2xl font-bold text-deep">Accès réservé</h1>
        <p className="text-slate-500 mt-2">Cette interface est réservée aux administrateurs Doctobook.</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FF7A5C] mb-2">Back-office · Partenaires</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-deep tracking-tight">Leads partenaires PACA</h1>
          <p className="text-sm text-slate-500 mt-1">Programme Doctobook Trans & Doctobook Rx · Pilote Q2 2026</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-leads"><RefreshCw className="h-4 w-4 mr-1.5" />Actualiser</Button>
          <Button size="sm" onClick={exportCSV} className="bg-[#2A4365] hover:bg-[#1e3050] text-white" data-testid="export-csv"><Download className="h-4 w-4 mr-1.5" />Export CSV</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total leads" value={items.length} icon={Filter} color="slate" />
        <StatCard label="Transport" value={counts.transport || 0} icon={Truck} color="emerald" />
        <StatCard label="Pharmacies" value={counts.pharmacy || 0} icon={Pill} color="coral" />
        <StatCard label="Signés" value={items.filter((l) => l.status === "signed").length} icon={Filter} color="blue" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap gap-2 items-center">
        <Input placeholder="Rechercher par structure, contact, email, ville…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" data-testid="search-input" />
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="filter-type">
          <option value="all">Tous types</option>
          <option value="transport">Transport</option>
          <option value="pharmacy">Pharmacie</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="filter-status">
          <option value="all">Tous statuts</option>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div className="ml-auto text-xs text-slate-500">{filtered.length} lead{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}</div>
      </div>

      {/* Leads list */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-white border border-slate-200 rounded-xl">Aucun lead pour ces critères.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((l) => <LeadRow key={l.lead_id} lead={l} onStatusChange={updateStatus} />)}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-slate-500 font-semibold uppercase">{label}</div>
          <div className="text-2xl font-extrabold text-deep mt-1" data-testid={`stat-${label.replace(/ /g, "-").toLowerCase()}`}>{value}</div>
        </div>
        <Icon className={`h-5 w-5 text-${color}-500`} />
      </div>
    </div>
  );
}

function LeadRow({ lead, onStatusChange }) {
  const statusMeta = STATUSES.find((s) => s.value === lead.status) || STATUSES[0];
  const isTransport = lead.partner_type === "transport";
  const TypeIcon = isTransport ? Truck : Pill;

  return (
    <article className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`lead-${lead.lead_id}`}>
      <header className="flex items-start gap-3 flex-wrap">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0 ${isTransport ? "bg-gradient-to-br from-emerald-600 to-emerald-800" : "bg-gradient-to-br from-[#FF7A5C] to-[#a8421f]"}`}>
          <TypeIcon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-deep">{lead.company_name}</div>
          <div className="text-xs text-slate-500">
            {isTransport ? "Doctobook Trans" : "Doctobook Rx"} · SIRET {lead.siret || "—"}
          </div>
        </div>
        <select
          value={lead.status}
          onChange={(e) => onStatusChange(lead.lead_id, e.target.value)}
          className={`text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 border ${statusMeta.color}`}
          data-testid={`status-${lead.lead_id}`}
        >
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </header>

      <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        <div className="flex items-center gap-2 text-slate-700"><Mail className="h-3.5 w-3.5 text-slate-400" /><a href={`mailto:${lead.email}`} className="hover:underline">{lead.email}</a></div>
        <div className="flex items-center gap-2 text-slate-700"><Phone className="h-3.5 w-3.5 text-slate-400" />{lead.phone || "—"}</div>
        <div className="flex items-center gap-2 text-slate-700"><MapPin className="h-3.5 w-3.5 text-slate-400" />{lead.city} {lead.postal_code || ""} · {lead.region || "—"}</div>
        <div className="flex items-center gap-2 text-slate-500"><Calendar className="h-3.5 w-3.5 text-slate-400" />{new Date(lead.created_at).toLocaleString("fr-FR")}</div>
      </div>

      <div className="mt-3 text-sm">
        <div className="text-xs font-semibold uppercase text-slate-500">Contact</div>
        <div className="text-slate-800">{lead.contact_name}</div>
      </div>

      {lead.fleet_or_size && (
        <div className="mt-2 text-sm">
          <div className="text-xs font-semibold uppercase text-slate-500">{isTransport ? "Flotte" : "Effectif"}</div>
          <div className="text-slate-800">{lead.fleet_or_size}</div>
        </div>
      )}

      {lead.message && (
        <blockquote className="mt-3 border-l-4 border-slate-200 pl-3 text-sm text-slate-600 italic">« {lead.message} »</blockquote>
      )}

      {lead.consent_communications && (
        <div className="mt-3 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-block">
          ✓ A accepté les communications
        </div>
      )}
    </article>
  );
}
